import React, { useEffect, useMemo, useState } from 'react';
import { Button, Input, Modal, Select, message } from 'antd';
import { useTranslation } from '../../../i18n';
import { scriptChannelMeta, type ScriptChannel, type ScriptTemplate } from './scriptData';

export type ScriptDraft = Pick<ScriptTemplate, 'name' | 'channel' | 'content'> & {
  id?: string;
  subject?: string;
};

type ScriptEditModalProps = {
  open: boolean;
  /** Without an `id` the modal creates a new template. */
  draft: ScriptDraft | null;
  onClose: () => void;
  onSave: (draft: ScriptDraft) => void | Promise<void>;
};

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className='flex items-center gap-3'>
      <div className='w-24 shrink-0 text-right text-sm font-medium text-slate-700'>{label}</div>
      <div className='min-w-0 flex-1'>{children}</div>
    </div>
  );
}

export function ScriptEditModal({ open, draft, onClose, onSave }: ScriptEditModalProps) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [channel, setChannel] = useState<ScriptChannel>('dm');
  const [subject, setSubject] = useState('');
  const [content, setContent] = useState('');
  const [saving, setSaving] = useState(false);

  const channelOptions = useMemo(
    () =>
      (Object.keys(scriptChannelMeta) as ScriptChannel[]).map((value) => ({
        value,
        label: t(`script.channel_${value}`)
      })),
    [t]
  );

  useEffect(() => {
    if (open) {
      setName(draft?.name ?? '');
      setChannel(draft?.channel ?? 'dm');
      setSubject(draft?.subject ?? '');
      setContent(draft?.content ?? '');
      setSaving(false);
    }
  }, [open, draft]);

  const submit = async () => {
    if (saving) return;
    const nextName = name.trim();
    const nextBody = content.trim();
    if (!nextName) {
      message.warning(t('script.name_required'));
      return;
    }
    if (!nextBody) {
      message.warning(t('script.content_required'));
      return;
    }
    const nextSubject = subject.trim();
    setSaving(true);
    try {
      await onSave({
        id: draft?.id,
        name: nextName,
        channel,
        content: nextBody,
        ...(nextSubject ? { subject: nextSubject.slice(0, 512) } : {})
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={draft?.id ? t('script.edit_title') : t('script.new_title')}
      open={open}
      onCancel={onClose}
      destroyOnClose
      centered
      width={560}
      footer={
        <div className='flex justify-end gap-2'>
          <Button className='rounded-lg' onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type='primary' className='wb-btn-primary rounded-lg' loading={saving} onClick={submit}>
            {t('common.save')}
          </Button>
        </div>
      }
    >
      <div className='space-y-4 pt-1'>
        <Field label={t('script.name')}>
          <Input
            value={name}
            maxLength={255}
            placeholder={t('script.name_placeholder')}
            onChange={(e) => setName(e.target.value)}
          />
        </Field>
        <Field label={t('script.channel')}>
          <Select className='w-full' value={channel} onChange={setChannel} options={channelOptions} />
        </Field>
        <Field label={t('script.subject')}>
          <Input
            value={subject}
            maxLength={512}
            placeholder={t('script.subject_placeholder')}
            onChange={(e) => setSubject(e.target.value)}
          />
        </Field>
        <Field label={t('script.body')}>
          <Input.TextArea
            value={content}
            autoSize={{ minRows: 4, maxRows: 10 }}
            placeholder={t('script.body_placeholder', { token: '{{name}}' })}
            onChange={(e) => setContent(e.target.value)}
          />
        </Field>
      </div>
    </Modal>
  );
}
